import { useRef } from "react";
import {
    motion,
    useScroll,
    useTransform,
    useMotionValue,
    useSpring,
} from "framer-motion";
import { BRANDS, ARTWORK_SRC, TAGLINE_LINES } from "../config/brands";
import Hotspot from "./Hotspot";
import RainCanvas from "./RainCanvas";

const EASE = [0.22, 1, 0.36, 1];

function MaskedLine({ children, delay }) {
    return (
        <span className="block overflow-hidden pb-1">
            <motion.span
                className="block"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1.1, ease: EASE, delay }}
            >
                {children}
            </motion.span>
        </span>
    );
}

export default function Hero({ onComingSoon }) {
    const sectionRef = useRef(null);
    const { scrollYProgress } = useScroll({
        target: sectionRef,
        offset: ["start start", "end start"],
    });
    const artY = useTransform(scrollYProgress, [0, 1], [0, 90]);
    const introY = useTransform(scrollYProgress, [0, 1], [0, -60]);

    const mx = useMotionValue(0);
    const my = useMotionValue(0);
    const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [2.2, -2.2]), {
        stiffness: 50,
        damping: 16,
    });
    const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-2.2, 2.2]), {
        stiffness: 50,
        damping: 16,
    });

    const handleMouseMove = (e) => {
        const rect = sectionRef.current?.getBoundingClientRect();
        if (!rect) return;
        mx.set((e.clientX - rect.left) / rect.width - 0.5);
        my.set((e.clientY - rect.top) / rect.height - 0.5);
    };

    return (
        <section
            ref={sectionRef}
            onMouseMove={handleMouseMove}
            className="relative overflow-hidden pt-28 pb-10 sm:pt-32"
            aria-label="Truth Excellence Group brands"
        >
            <div
                className="pointer-events-none absolute inset-0"
                aria-hidden="true"
                style={{
                    background:
                        "radial-gradient(60% 45% at 50% 0%, rgba(212,175,55,0.10), transparent 70%), radial-gradient(45% 40% at 85% 60%, rgba(0,122,255,0.08), transparent 70%), radial-gradient(45% 40% at 15% 60%, rgba(0,122,255,0.06), transparent 70%)",
                }}
            />
            <RainCanvas className="pointer-events-none absolute inset-0 h-full w-full" />

            <motion.div style={{ y: introY }} className="relative z-10 mx-auto max-w-5xl px-5 text-center">
                <motion.p
                    initial={{ opacity: 0, letterSpacing: "0.6em" }}
                    animate={{ opacity: 1, letterSpacing: "0.35em" }}
                    transition={{ duration: 1.2, ease: EASE }}
                    className="eyebrow mb-6"
                >
                    Truth Excellence Group LLC
                </motion.p>

                <h1 className="font-display text-4xl leading-[1.08] font-medium tracking-tight sm:text-5xl lg:text-6xl">
                    <MaskedLine delay={0.25}>
                        <span className="text-slate-100">{TAGLINE_LINES[0]}</span>
                    </MaskedLine>
                    <MaskedLine delay={0.4}>
                        <span className="text-gold-gradient">{TAGLINE_LINES[1]}</span>
                    </MaskedLine>
                    <MaskedLine delay={0.55}>
                        <span className="text-slate-100">{TAGLINE_LINES[2]}</span>
                    </MaskedLine>
                </h1>
            </motion.div>

            <motion.div style={{ y: artY }} className="relative z-10 mx-auto mt-12 max-w-6xl px-4 sm:px-8">
                <motion.div
                    id="brands"
                    initial={{ opacity: 0, y: 50, scale: 1.015 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 1.2, ease: EASE, delay: 0.75 }}
                    style={{ rotateX, rotateY, transformPerspective: 1400 }}
                    className="artwork-frame scroll-mt-28"
                >
                    <img
                        src={ARTWORK_SRC}
                        alt="Truth Excellence Group umbrella artwork featuring six brands: Truth Insurance, The Truth Spot, Truth Travels, Truth Fleet, Activate The Crowd, and Spit Your Truth"
                        className="block h-auto w-full rounded-[18px]"
                        width={1430}
                        height={1100}
                        draggable="false"
                        fetchpriority="high"
                        data-testid="teg-hero-umbrella-artwork"
                    />
                    <div className="absolute inset-0 z-10">
                        {BRANDS.map((brand) => (
                            <Hotspot key={brand.id} brand={brand} onComingSoon={onComingSoon} />
                        ))}
                    </div>
                </motion.div>

                <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 1, delay: 1.6 }}
                    className="font-mono-lux mt-6 text-center text-[10px] tracking-[0.3em] text-slate-500 uppercase"
                >
                    Hover a brand beneath the umbrella — or scroll
                </motion.p>
            </motion.div>
        </section>
    );
}
